import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function RecordDialog({ open, onOpenChange, fields, title, initial, endpoint, method = "post", onSaved }) {
  const { config } = useAuth();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [lookups, setLookups] = useState({ owners: [], carriers: [], parties: [] });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm(initial || {});
      setErrors({});
      const needs = new Set(fields.map((f) => f.lookup).filter(Boolean));
      needs.forEach(async (name) => {
        try {
          if (name === "parties") {
            const { data } = await api.get("/parties?page_size=200");
            setLookups((l) => ({ ...l, parties: data.items }));
          } else {
            const { data } = await api.get(`/lookups/${name}`);
            setLookups((l) => ({ ...l, [name]: data.items }));
          }
        } catch {}
      });
    }
  }, [open]); // eslint-disable-line

  const resolveOptions = (f) => {
    const o = f.options || {};
    if (o.static) return o.static.map((v) => (typeof v === "object" ? v : { value: v, label: v }));
    if (o.config) return (config[o.config] || []).map((v) => ({ value: v, label: v }));
    if (o.configLines) {
      const lines = config.lines_of_business || {};
      const out = [];
      Object.values(lines).forEach((arr) => (arr || []).forEach((v) => out.push({ value: v, label: v })));
      return out;
    }
    return [];
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const errs = {};
    fields.forEach((f) => {
      if (f.required && !form[f.key]) errs[f.key] = "Required";
    });
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Please complete the required fields.");
      return;
    }
    setSaving(true);
    try {
      const { data } = method === "put" ? await api.put(endpoint, form) : await api.post(endpoint, form);
      toast.success("Saved successfully.");
      onOpenChange(false);
      onSaved && onSaved(data);
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.detail?.conflict_ref) {
        const d = e.response.data.detail;
        toast.error(`Edit conflict (${d.conflict_ref})`, {
          description: "This record was changed by someone else. Your changes were not saved. Resolve it in the Conflict Center.",
          action: { label: "Conflict Center", onClick: () => { window.location.href = "/conflicts"; } },
          duration: 8000,
        });
        onOpenChange(false);
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not save.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto end-scroll bg-white" data-testid="record-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-navy">{title}</DialogTitle>
          <DialogDescription className="text-xs text-mutedsea">Complete the fields below. Required fields are marked with an asterisk.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-x-5 gap-y-3 py-2">
          {fields.map((f) => {
            const full = f.type === "textarea";
            return (
              <div key={f.key} className={full ? "col-span-2" : ""}>
                <Label className="text-xs font-medium text-slate mb-1 block">
                  {f.label}{f.required && <span className="text-seared ml-0.5">*</span>}
                </Label>
                {f.type === "textarea" ? (
                  <Textarea data-testid={`field-${f.key}`} value={form[f.key] || ""} rows={2}
                    onChange={(e) => set(f.key, e.target.value)} className="text-sm" />
                ) : f.type === "switch" ? (
                  <div className="flex items-center h-9">
                    <Switch data-testid={`field-${f.key}`} checked={!!form[f.key]} onCheckedChange={(v) => set(f.key, v)} />
                  </div>
                ) : f.type === "select" ? (
                  <Select value={form[f.key] || ""} onValueChange={(v) => set(f.key, v)}>
                    <SelectTrigger data-testid={`field-${f.key}`} className="text-sm bg-white"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      {resolveOptions(f).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : f.type === "party" ? (
                  <Select value={form[f.key] || ""} onValueChange={(v) => set(f.key, v)}>
                    <SelectTrigger data-testid={`field-${f.key}`} className="text-sm bg-white"><SelectValue placeholder="Select client…" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      {lookups.parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.display_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : f.type === "producer" ? (
                  <Select value={form[f.key] || ""} onValueChange={(v) => set(f.key, v)}>
                    <SelectTrigger data-testid={`field-${f.key}`} className="text-sm bg-white"><SelectValue placeholder="Select producer…" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      {lookups.owners.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : f.type === "owner" ? (
                  <Select value={form[f.key] || ""} onValueChange={(v) => set(f.key, v)}>
                    <SelectTrigger data-testid={`field-${f.key}`} className="text-sm bg-white"><SelectValue placeholder="Select owner…" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      {lookups.owners.map((u) => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : f.type === "carrier" ? (
                  <Select value={form[f.key] || ""} onValueChange={(v) => set(f.key, v)}>
                    <SelectTrigger data-testid={`field-${f.key}`} className="text-sm bg-white"><SelectValue placeholder="Select carrier…" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      {lookups.carriers.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input data-testid={`field-${f.key}`} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                    value={form[f.key] || ""} onChange={(e) => set(f.key, f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
                    className={`text-sm bg-white ${errors[f.key] ? "border-seared" : ""}`} />
                )}
                {errors[f.key] && <p className="text-xs text-seared mt-0.5">{errors[f.key]}</p>}
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="dialog-save" className="bg-navy hover:bg-atlantic text-white">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
